import { useKeycloak } from '@react-keycloak/web';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useTheme } from '@mui/material';

import { UseMutateFunction } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { UserInfo } from '@/api/data-contracts';
import { ReactComponent as DeleteIcon } from '@/assets/icons/DeleteRounded.svg';
import { ReactComponent as EditIcon } from '@/assets/icons/EditRounded.svg';
import { ReactComponent as MarkAsTaskIcon } from '@/assets/icons/MarkAsTaskRounded.svg';
import { ReactComponent as ReplyIcon } from '@/assets/icons/ReplyRounded.svg';
import CommentOrTaskForm from '@/features/comments/ui/CommentOrTaskForm';
import AuthorChips from '@/features/comments/ui/Comments/AuthorChips';
import Reply from '@/features/comments/ui/Comments/CommentReply';
import { CommentWithUnread } from '@/features/comments/ui/Comments/commentUtils';
import Avatar from '@/shared/ui/Avatar';
import ConfirmationDialog from '@/shared/ui/ConfirmationDialog';

import {
  BodyText,
  CommentHeader,
  Container,
  ContentFooter,
  ContentSection,
  FooterButton,
  FooterButtonText,
  HeaderSection,
  RepliesList,
  TimeAgo,
  UserName,
} from './Comment.styles';
import CommentAttachments from './CommentAttachments';
import { parseCommentMessage } from './commentMessage';
import useAuthorInfo from './useAuthorInfo';

const ICON_STYLE = { width: '11px', height: '11px', flexShrink: 0 } as const;

interface Props {
  comment: CommentWithUnread;
  replies?: CommentWithUnread[];
  commentsThreadId?: string;
  onTaskSave?: (content: string, priority?: 'LOW' | 'MEDIUM' | 'HIGH') => void;
  onMarkAsTask?: (message: string) => void;
  onCommentSave: (params: {
    message: string;
    files?: File[];
    commentId?: string;
    parentId?: string;
    edit?: boolean;
  }) => void;
  deleteComment?: UseMutateFunction<void, unknown, string, unknown>;
  allUsers: UserInfo[];
  highlightCommentId?: string;
  downloadCommentFile?: (resourceId: string, documentId: string) => Promise<Blob>;
}

const Comment: FC<Props> = ({
  comment,
  replies,
  commentsThreadId,
  onCommentSave,
  deleteComment,
  onTaskSave,
  onMarkAsTask,
  allUsers,
  highlightCommentId,
  downloadCommentFile,
}) => {
  const { keycloak } = useKeycloak();
  const { t } = useTranslation();
  const theme = useTheme();

  const { avatarColor, authorRole, platformChipInfo } = useAuthorInfo({
    authorId: comment.author.id,
    authorUserType: comment.author.userType,
    authorPosition: comment.author.position,
    allUsers,
  });

  const highlighted = highlightCommentId === comment.id;
  const [isHighlighted, setIsHighlighted] = useState(highlighted);

  const highlightRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!highlighted) return;
    setIsHighlighted(true);
    const rafId = requestAnimationFrame(() => {
      const el = highlightRef.current;
      if (!el) return;
      let container: HTMLElement | null = el.parentElement;
      while (container) {
        const { overflowY } = window.getComputedStyle(container);
        if (overflowY === 'auto' || overflowY === 'scroll') break;
        container = container.parentElement;
      }
      if (container) {
        const elTop = el.getBoundingClientRect().top - container.getBoundingClientRect().top;
        container.scrollTo({
          top: container.scrollTop + elTop - container.clientHeight / 2 + el.clientHeight / 2,
          behavior: 'smooth',
        });
      } else {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
    const timer = setTimeout(() => setIsHighlighted(false), 3000);
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timer);
    };
  }, [highlighted]);

  const [showReplyForm, setShowReplyForm] = useState(false);
  const [, setReplyFormVisible] = useState(false);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const currentUserId = keycloak.tokenParsed?.sub as string | undefined;
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const author = useMemo(() => comment.author, [comment.author]);

  const renderMessage = useCallback(() => {
    const parsed = parseCommentMessage(comment.message);
    if (typeof parsed === 'string') return parsed;
    return parsed.map((segment) => (
      <span key={segment.key} style={segment.style}>
        {segment.text}
      </span>
    ));
  }, [comment.message]);

  const renderButtons = useCallback(
    () => (
      <>
        {!commentsThreadId && (
          <FooterButton
            onClick={() => {
              setIsEdit(false);
              setShowReplyForm((prev) => !prev);
              setReplyFormVisible((prev) => !prev);
            }}
          >
            <ReplyIcon style={ICON_STYLE} />
            <FooterButtonText>{t('Reply')}</FooterButtonText>
          </FooterButton>
        )}

        {comment.author.id === currentUserId && (
          <FooterButton
            onClick={() => {
              setIsEdit(true);
              setShowReplyForm((prev) => !prev);
              setReplyFormVisible((prev) => !prev);
            }}
          >
            <EditIcon style={ICON_STYLE} />
            <FooterButtonText>{t('Edit')}</FooterButtonText>
          </FooterButton>
        )}

        {comment.author.id === currentUserId && deleteComment && (
          <FooterButton
            onClick={() => {
              setShowDeleteConfirmation(true);
            }}
          >
            <DeleteIcon style={ICON_STYLE} />
            <FooterButtonText>{t('Delete')}</FooterButtonText>
          </FooterButton>
        )}

        {!!(onMarkAsTask || onTaskSave) && (
          <FooterButton
            onClick={() =>
              onMarkAsTask ? onMarkAsTask(comment.message ?? '') : setShowTaskForm((prev) => !prev)
            }
          >
            <MarkAsTaskIcon style={ICON_STYLE} />
            <FooterButtonText>{t('Mark as task')}</FooterButtonText>
          </FooterButton>
        )}
      </>
    ),
    [
      comment.author.id,
      comment.message,
      commentsThreadId,
      currentUserId,
      deleteComment,
      onMarkAsTask,
      onTaskSave,
      t,
    ],
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const [replyPositions, setReplyPositions] = useState<number[]>([]);

  useEffect(() => {
    if (!replies?.length || !containerRef.current || !currentUserId) {
      setReplyPositions([]);
      return;
    }

    const rafId = requestAnimationFrame(() => {
      const container = containerRef.current;
      if (!container) return;

      const repliesList = container.querySelector('[data-replies-list]') as HTMLElement;
      if (!repliesList || !repliesList.children.length) return;

      const containerTop = container.getBoundingClientRect().top;
      const avatarBottom = 22;
      const positions: number[] = [];

      for (let i = 0; i < repliesList.children.length; i++) {
        const replyEl = repliesList.children[i] as HTMLElement;
        const replyAvatar = replyEl.querySelector('[class*="MuiAvatar"]') as HTMLElement;
        const avatarCenterY = replyAvatar
          ? replyAvatar.getBoundingClientRect().top - containerTop + 11
          : repliesList.offsetTop + replyEl.offsetTop + 11;
        positions.push(avatarCenterY - avatarBottom);
      }

      setReplyPositions(positions);
    });

    return () => cancelAnimationFrame(rafId);
  }, [replies?.length, showReplyForm, showTaskForm, currentUserId]);

  const mergedRef = (el: HTMLDivElement | null) => {
    (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
    (highlightRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
  };

  return (
    <Container
      ref={mergedRef}
      data-testid="comment"
      data-comment-id={comment.id}
      data-highlighted={highlighted}
      style={{
        borderRadius: '8px',
        boxShadow: isHighlighted
          ? `0 0 0 2px ${theme.palette.accent.main}`
          : '0 0 0 2px transparent',
        transition: 'box-shadow 1s ease-in-out',
      }}
    >
      {replyPositions.length > 0 &&
        (() => {
          const lastY = replyPositions[replyPositions.length - 1];
          const r = 6;
          const hEnd = 12;
          const svgHeight = lastY + 1;

          const verticalLine = `M0.5 4V${lastY - r}`;

          const branches = replyPositions.map(
            (ry) =>
              `M0.5 ${ry - r}C0.5 ${ry - r + r * 0.55} ${0.5 + r * 0.45} ${ry} ${r + 0.5} ${ry}H${hEnd}`,
          );

          return (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width={hEnd + 1}
              height={svgHeight}
              viewBox={`0 0 ${hEnd + 1} ${svgHeight}`}
              fill="none"
              style={{
                position: 'absolute',
                left: '11px',
                top: '22px',
                pointerEvents: 'none',
                overflow: 'visible',
              }}
            >
              <path d={verticalLine} stroke="#737085" strokeWidth="1" fill="none" />
              {branches.map((d, i) => (
                <path key={i} d={d} stroke="#737085" strokeWidth="1" fill="none" />
              ))}
            </svg>
          );
        })()}

      <Avatar
        sx={{
          width: '22px',
          height: '22px',
          minWidth: '22px',
          minHeight: '22px',
          backgroundColor: avatarColor,
        }}
        userId={author?.id ?? null}
        name={`${author.firstName} ${author.lastName}`}
      />

      <ContentSection>
        <CommentHeader>
          <HeaderSection>
            <UserName>{`${author.firstName} ${author.lastName}`}</UserName>
            <AuthorChips platformChipInfo={platformChipInfo} authorRole={authorRole} />
            <TimeAgo>{dayjs(comment.created).fromNow()}</TimeAgo>
          </HeaderSection>
        </CommentHeader>

        <BodyText>{renderMessage()}</BodyText>

        {!!comment.files?.length && downloadCommentFile && (
          <CommentAttachments
            files={comment.files}
            resourceId={comment.resourceId}
            downloadCommentFile={downloadCommentFile}
          />
        )}

        <ContentFooter>{renderButtons()}</ContentFooter>

        {(showReplyForm || showTaskForm) && (
          <ContentFooter sx={{ width: '100%' }}>
            <CommentOrTaskForm
              type={showReplyForm ? 'comment' : 'task'}
              greenVariant={showReplyForm || showTaskForm}
              buttonLabel={t(showTaskForm ? 'Add task' : isEdit ? 'Save changes' : 'Add reply')}
              isReply={showReplyForm}
              isEdit={isEdit}
              commentId={comment.id}
              parentId={comment.id}
              onCommentSave={onCommentSave}
              onTaskSave={onTaskSave}
              onCancel={() => {
                setShowReplyForm(false);
                setShowTaskForm(false);
                setReplyFormVisible(false);
              }}
              comment={comment.message}
            />
          </ContentFooter>
        )}

        {!!replies?.length && !!currentUserId && (
          <RepliesList data-replies-list>
            {replies.map((reply, key) => (
              <Reply
                key={key}
                reply={reply}
                commentsThreadId={commentsThreadId}
                onCommentSave={onCommentSave}
                onTaskSave={onTaskSave}
                onMarkAsTask={onMarkAsTask}
                deleteComment={deleteComment}
                allUsers={allUsers}
                currentUserId={currentUserId}
                setReplyFormVisible={setReplyFormVisible}
                highlighted={reply.id === highlightCommentId}
                downloadCommentFile={downloadCommentFile}
              />
            ))}
          </RepliesList>
        )}
      </ContentSection>

      {showDeleteConfirmation && (
        <ConfirmationDialog
          open={showDeleteConfirmation}
          handleClose={() => setShowDeleteConfirmation(false)}
          handleConfirm={() => {
            setShowDeleteConfirmation(false);
            deleteComment?.(comment.id);
          }}
          title={t('Delete comment')}
          description={t('Are you sure you want to delete this comment?')}
          cancelLabel={t('No')}
        />
      )}
    </Container>
  );
};

export default Comment;
